import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";

const ROLES = ["super_admin", "admin", "manager", "recruiter", "candidate"];

export default function AdminUsers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));

  useEffect(() => {
    load();
  }, []);

  const update = async (id, payload) => {
    try {
      await api.put(`/admin/users/${id}`, payload);
      toast.success(t("common.save"));
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="font-display text-3xl font-light sm:text-4xl">{t("admin.usersTitle")}</h1>
      <p className="mt-2 text-xs text-muted-foreground">{t("admin.lastSuperAdmin")}</p>

      <div className="mt-6 overflow-x-auto border border-border bg-surface">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="p-3 font-medium">{t("admin.table.name")}</th>
              <th className="p-3 font-medium">{t("auth.email")}</th>
              <th className="p-3 font-medium">{t("admin.role")}</th>
              <th className="p-3 font-medium">{t("admin.status")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((row) => (
              <tr key={row.id} data-testid={`user-row-${row.id}`} className="border-t border-border">
                <td className="p-3">
                  {row.first_name} {row.last_name}
                  {row.is_demo && <span className="ml-2 text-[10px] uppercase tracking-widest text-accent">demo</span>}
                </td>
                <td className="p-3 text-muted-foreground">{row.email}</td>
                <td className="p-3">
                  <select
                    data-testid={`user-role-${row.id}`}
                    value={row.role}
                    disabled={user?.role !== "super_admin" && (row.role === "super_admin" || row.id === user?.id)}
                    onChange={(event) => update(row.id, { role: event.target.value })}
                    className="h-9 border border-input bg-surface px-2 text-sm"
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3">
                  <button
                    data-testid={`user-toggle-${row.id}`}
                    onClick={() => update(row.id, { is_active: !row.is_active })}
                    className={`text-xs ${row.is_active ? "text-accent" : "text-muted-foreground"} hover:underline`}
                  >
                    {row.is_active ? t("admin.activeLabel") : t("admin.inactiveLabel")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
